import React, { useState } from 'react';
import { X, Crown, Calendar, AlertCircle, Loader2 } from 'lucide-react';
import { BillingService } from '@/services/billingService';
import { CacheService } from '@/services/cacheService';

interface SubscriptionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  isDark: boolean;
  userEmail?: string;
  proExpiresAt?: string | null;
  onRefresh?: () => void;
  isPro?: boolean;
}

export const SubscriptionDetailsModal: React.FC<SubscriptionDetailsModalProps> = ({
  isOpen,
  onClose,
  isDark,
  userEmail,
  proExpiresAt,
  onRefresh,
  isPro
}) => {
  const [loading, setLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  if (!isOpen) return null;

  const expiryDate = proExpiresAt ? new Date(proExpiresAt) : null;
  const today = new Date();
  const daysRemaining = expiryDate ? Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)) : 0;
  const isExpiringSoon = daysRemaining > 0 && daysRemaining <= 7;
  const isExpired = daysRemaining <= 0;

  const handleCancelSubscription = async () => {
    if (!userEmail) return;
    setLoading(true);
    try {
      // Update user_profiles to set is_pro = false
      const { error } = await (window as any).supabase
        .from('user_profiles')
        .update({
          is_pro: false,
          pro_expires_at: null
        })
        .eq('email', userEmail);

      if (error) {
        alert('❌ Error cancelling subscription: ' + error.message);
      } else {
        console.log('✅ Subscription cancelled successfully');
        // Invalidate cache
        CacheService.invalidateCategory('subscription');
        // Show success message
        alert('✅ Your Pro subscription has been cancelled.');
        // Refresh subscription data
        onRefresh?.();
        setShowCancelConfirm(false);
        onClose();
      }
    } catch (err) {
      console.error('Error:', err);
      alert('❌ An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
        <div
          className={`w-full max-w-md max-h-[90vh] rounded-2xl sm:rounded-3xl shadow-2xl transition-all transform scale-100 my-4 sm:my-0 overflow-y-auto ${
            isDark ? 'bg-zinc-900 border border-zinc-800' : 'bg-white border border-zinc-200'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`flex items-center justify-between p-4 sm:p-6 border-b ${isDark ? 'border-zinc-800' : 'border-zinc-200'} flex-shrink-0`}>
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0">
                <Crown className="w-4 sm:w-5 h-4 sm:h-5 text-white fill-white" />
              </div>
              <h2 className={`text-lg sm:text-xl font-bold truncate ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                Pro Subscription
              </h2>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                isDark ? 'hover:bg-zinc-800' : 'hover:bg-zinc-100'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 180px)' }}>
            {isPro ? (
              <>
                {/* Subscription Status */}
                <div className={`p-3 sm:p-4 rounded-lg sm:rounded-xl ${isDark ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-200'}`}>
                  <div className="flex items-start gap-2 sm:gap-3">
                    <Crown className="w-4 sm:w-5 h-4 sm:h-5 text-emerald-500 mt-0.5 flex-shrink-0 fill-current" />
                    <div className="min-w-0">
                      <p className={`font-semibold text-sm sm:text-base ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                        Active Pro Member
                      </p>
                      <p className={`text-xs sm:text-sm mt-1 ${isDark ? 'text-emerald-300/80' : 'text-emerald-600/80'}`}>
                        Full access to all Pro features
                      </p>
                    </div>
                  </div>
                </div>

                {/* Expiry Information */}
                <div className="space-y-2 sm:space-y-3">
                  <label className={`text-xs sm:text-sm font-semibold flex items-center gap-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                    <Calendar className="w-3 sm:w-4 h-3 sm:h-4 text-red-500 flex-shrink-0" />
                    Expiration Date
                  </label>
                  <div className={`p-3 sm:p-4 rounded-lg sm:rounded-lg ${isDark ? 'bg-zinc-800/50 border border-zinc-700' : 'bg-zinc-50 border border-zinc-200'}`}>
                    {expiryDate ? (
                      <div>
                        <p className={`font-semibold text-sm sm:text-base ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                          {expiryDate.toLocaleDateString('en-US', { 
                            weekday: 'short', 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </p>
                        <p className={`text-xs sm:text-sm mt-1 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                          {isExpired ? (
                            <span className="text-red-500 font-semibold">❌ Expired</span>
                          ) : isExpiringSoon ? (
                            <span className="text-amber-500 font-semibold">⚠️ {daysRemaining} days left</span>
                          ) : (
                            <span>⏳ {daysRemaining} days remaining</span>
                          )}
                        </p>
                      </div>
                    ) : (
                      <p className={`text-xs sm:text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>No expiry date set</p>
                    )}
                  </div>
                </div>

                {/* Expiring Soon Warning */}
                {isExpiringSoon && !isExpired && (
                  <div className={`p-3 rounded-lg flex gap-2 sm:gap-3 ${isDark ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'}`}>
                    <AlertCircle className={`w-4 sm:w-5 h-4 sm:h-5 flex-shrink-0 mt-0.5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                    <p className={`text-xs sm:text-sm ${isDark ? 'text-amber-300' : 'text-amber-600'}`}>
                      Your subscription is expiring soon. Renew now!
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Pending Status */}
                <div className={`p-3 sm:p-4 rounded-lg sm:rounded-xl ${isDark ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'}`}>
                  <div className="flex items-start gap-2 sm:gap-3">
                    <AlertCircle className="w-4 sm:w-5 h-4 sm:h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className={`font-semibold text-sm sm:text-base ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                        Upgrade Pending Review
                      </p>
                      <p className={`text-xs sm:text-sm mt-1 ${isDark ? 'text-amber-300/80' : 'text-amber-600/80'}`}>
                        Your upgrade request is being reviewed by our admins
                      </p>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <div className={`p-3 sm:p-4 rounded-lg sm:rounded-lg ${isDark ? 'bg-zinc-800/50 border border-zinc-700' : 'bg-zinc-50 border border-zinc-200'}`}>
                  <p className={`text-xs sm:text-sm font-semibold mb-3 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                    Typical Processing Time
                  </p>
                  <p className={`text-xs sm:text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                    ⏱️ Your request is typically processed within 1-2 hours. Once approved, you'll have instant access to all Pro features.
                  </p>
                </div>
              </>
            )}

            {/* Pro Features */}
            <div className="space-y-2 sm:space-y-2 pt-2 sm:pt-4">
              <label className={`text-xs sm:text-sm font-semibold block ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                Pro Features
              </label>
              <ul className="space-y-1 sm:space-y-2">
                {[
                  '✅ Unlimited roadmaps',
                  '✅ Expert level access',
                  '✅ Advanced analytics',
                  '✅ Priority support'
                ].map((feature, idx) => (
                  <li key={idx} className={`text-xs sm:text-sm ${isDark ? 'text-zinc-300' : 'text-zinc-600'}`}>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Actions */}
          <div className={`flex flex-col gap-2 sm:gap-3 p-4 sm:p-6 border-t ${isDark ? 'border-zinc-800' : 'border-zinc-200'} flex-shrink-0`}>
            {/* Cancel Subscription Button */}
            {!showCancelConfirm && (
              <button
                onClick={() => setShowCancelConfirm(true)}
                disabled={loading}
                className={`w-full py-2 sm:py-3 rounded-lg font-semibold text-xs sm:text-sm transition-all ${
                  isDark
                    ? 'bg-red-500/10 hover:bg-red-500/20 disabled:opacity-50 text-red-400 border border-red-500/20 hover:border-red-500/50'
                    : 'bg-red-50 hover:bg-red-100 disabled:opacity-50 text-red-600 border border-red-200 hover:border-red-300'
                }`}
              >
                ❌ Cancel
              </button>
            )}

            {/* Cancel Confirmation */}
            {showCancelConfirm && (
              <div className="space-y-2 sm:space-y-3">
                <div className={`p-3 rounded-lg ${isDark ? 'bg-red-500/10 border border-red-500/20' : 'bg-red-50 border border-red-200'}`}>
                  <p className={`text-xs sm:text-sm font-semibold ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                    ⚠️ Sure? You'll lose Pro access.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleCancelSubscription}
                    disabled={loading}
                    className={`flex-1 py-2 rounded-lg font-semibold text-xs sm:text-sm transition-all ${
                      isDark
                        ? 'bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white'
                        : 'bg-red-500 hover:bg-red-600 disabled:bg-red-500/50 text-white'
                    }`}
                  >
                    {loading ? <Loader2 className="w-3 sm:w-4 h-3 sm:h-4 animate-spin mx-auto" /> : 'Yes, Cancel'}
                  </button>
                  <button
                    onClick={() => setShowCancelConfirm(false)}
                    disabled={loading}
                    className={`flex-1 py-2 rounded-lg font-semibold text-xs sm:text-sm transition-all ${
                      isDark
                        ? 'bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50 text-white'
                        : 'bg-zinc-200 hover:bg-zinc-300 disabled:opacity-50 text-zinc-900'
                    }`}
                  >
                    Keep Pro
                  </button>
                </div>
              </div>
            )}

            {/* Close Button */}
            {!showCancelConfirm && (
              <button
                onClick={onClose}
                className={`w-full py-2 rounded-lg font-semibold text-xs sm:text-sm transition-all ${
                  isDark
                    ? 'bg-zinc-700 hover:bg-zinc-600 text-white'
                    : 'bg-zinc-200 hover:bg-zinc-300 text-zinc-900'
                }`}
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
